<script lang="ts">
  /**
   * The Exposure cell: Public / Externally shared / Public + External, in the same tint the matching counter
   * card uses. A document in both kinds always reads "Public + External" rather than either single kind.
   *
   * One line: a tone pill carrying the kind label alone (#2244, reversing #2229's second count
   * line). Prefixing the external-principal count to the label ("Externally shared · 2 external")
   * made the pill wider than the table's 9rem Exposure column, and a second line under it made
   * rows uneven; the longest label, "● Externally shared", fits the column on its own. The exact
   * count and the kind are in the tooltip, and the detail panel lists the principals themselves.
   */
  import { formatNumberWithCommas } from "$lib/utils/number";

  import {
    exposureFilterLabel,
    exposureKindsOf,
    type ExposedSensitiveDocument,
    type ExposureFilter,
  } from "../exposedSensitiveDocuments";
  import { EXPOSURE_TONE } from "./exposureTones";

  let { row }: { row: ExposedSensitiveDocument } = $props();

  const kinds = $derived(exposureKindsOf(row));
  const filter = $derived<ExposureFilter>(kinds.length === 2 ? "both" : (kinds[0] ?? "public"));
  const tone = $derived(EXPOSURE_TONE[filter]);
  /** The tooltip's count, exact however large. */
  const exactExternals = $derived(
    `${formatNumberWithCommas(row.externalPrincipalCount)}${row.externalPrincipalsTruncated ? "+" : ""}`,
  );
  const principals = $derived(
    `${exactExternals} external ${row.externalPrincipalCount === 1 ? "principal" : "principals"}`,
  );
  const description = $derived(
    filter === "public"
      ? "Public: anyone with the link can open it"
      : filter === "external"
        ? `Externally shared: ${principals} can open it`
        : `Public + External: anyone with the link, and ${principals}, can open it`,
  );
</script>

<span
  class="inline-flex min-w-0 max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold {tone.badge}"
  title={description}
  data-testid="exposure-badge"
>
  <span class="h-1.5 w-1.5 shrink-0 rounded-full {tone.dot}"></span>
  <span class="truncate">{exposureFilterLabel(filter)}</span>
</span>
